import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Send, Smartphone, MessageSquare, AlertCircle, CheckCircle, User, Hash, Globe, Eye, Plus, FileText, LayoutTemplate } from 'lucide-react';
import './WhatsAppTest.css';

interface TemplateVariable {
    key: string;
    label: string;
    placeholder: string;
    component: string;
}

interface TemplateComponent {
    type: string;
    text?: string;
    format?: string;
}

interface WhatsAppTemplate {
    id: string;
    name: string;
    language: string;
    category: string;
    status: string;
    components: TemplateComponent[];
    parsedVariables?: TemplateVariable[];
}

export default function WhatsAppTest() {
    const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
    const [templateId, setTemplateId] = useState('');
    const [phone, setPhone] = useState('');
    const [vars, setVars] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [status, setStatus] = useState<{ type: 'success' | 'error', msg: string, details?: any } | null>(null);

    // Create Template State
    const [showCreateMode, setShowCreateMode] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateCategory, setNewTemplateCategory] = useState('MARKETING');
    const [newTemplateLang, setNewTemplateLang] = useState('es_AR');
    const [newTemplateBody, setNewTemplateBody] = useState('');
    const [creatingTemplate, setCreatingTemplate] = useState(false);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const { data, error } = await supabase.functions.invoke('whatsapp-templates', {
                method: 'GET'
            });

            if (error) throw error;
            if (data?.success && data?.data) {
                const parsedTemplates = data.data.map((t: any) => {
                    const parsedVariables: TemplateVariable[] = [];
                    // Extract parameters from BODY component (like {{1}}, {{nombre}})
                    const bodyComp = t.components.find((c: any) => c.type === 'BODY');
                    if (bodyComp && bodyComp.text) {
                        const matches = bodyComp.text.match(/\{\{([^}]+)\}\}/g);
                        if (matches) {
                            // Maintain order of appearance, remove duplicates
                            const uniqueMatches = Array.from(new Set<string>(matches.map((m: string) => m.replace(/[{}]/g, ''))));
                            uniqueMatches.forEach((paramName: string) => {
                                parsedVariables.push({
                                    key: paramName,
                                    label: `Dato para: {{${paramName}}}`,
                                    placeholder: `Ingresá el valor para ${paramName}`,
                                    component: 'body'
                                });
                            });
                        }
                    }
                    return { ...t, parsedVariables };
                });

                setTemplates(parsedTemplates);
                if (parsedTemplates.length > 0) {
                    setTemplateId(parsedTemplates[0].id);
                }
            } else {
                throw new Error(data?.error || 'Error fetching templates');
            }
        } catch (err: any) {
            console.error('Error loading templates:', err);
            setStatus({ type: 'error', msg: 'Error al cargar plantillas: ' + err.message });
        } finally {
            setLoadingTemplates(false);
        }
    };

    const selectedTemplate = templates.find(t => t.id === templateId);

    const handleTemplateChange = (id: string) => {
        setTemplateId(id);
        const next = templates.find(t => t.id === id);
        if (next) {
            setVars({});
            setStatus(null);
        }
    };

    const handleSendTest = async () => {
        if (!phone) {
            setStatus({ type: 'error', msg: 'Ingresa un teléfono' });
            return;
        }

        if (!selectedTemplate) return;

        setLoading(true);
        setStatus(null);

        try {
            // Build variables list correctly mapping the dynamic params
            // Note: Meta expects an ordered list for POSITIONAL formatting
            let variableObjects: any[] = [];

            if (selectedTemplate.parsedVariables && selectedTemplate.parsedVariables.length > 0) {
                // Send variables exactly in the order they appear in parsedVariables
                variableObjects = selectedTemplate.parsedVariables.map(v => ({
                    name: v.key,
                    value: vars[v.key] || ''
                }));
            }

            const payload = {
                phone: phone.replace(/\D/g, ''),
                templateName: selectedTemplate.name,
                language: selectedTemplate.language,
                variables: variableObjects
            };

            const { data, error } = await supabase.functions.invoke('whatsapp-proxy', {
                body: payload,
            });

            if (error) throw error;

            if (data?.success) {
                setStatus({ type: 'success', msg: '¡Mensaje enviado con éxito!' });
                // Dejar rastro en whatsapp_logs: sin esto el envío manual no aparecía en la
                // card ni en la bandeja, y el agente no sabía que ya le habíamos escrito.
                try {
                    // Renderizamos el body del template con las variables, como hace el webhook.
                    let text = selectedTemplate.components.find(c => c.type === 'BODY')?.text || '';
                    for (const v of selectedTemplate.parsedVariables || []) {
                        text = text.split(`{{${v.key}}}`).join(vars[v.key] || '');
                    }
                    await supabase.from('whatsapp_logs').insert({
                        direction: 'outgoing',
                        phone: phone.replace(/\D/g, ''),
                        template_name: selectedTemplate.name,
                        message_text: text || null,
                        status: 'sent',
                        meta_message_id: data?.data?.messages?.[0]?.id || null,
                        is_agent: false,
                    });
                } catch (logErr) {
                    console.error('No se pudo registrar el envío en whatsapp_logs:', logErr);
                }
            } else {
                throw new Error(data?.error || 'Error en el envío');
            }
        } catch (err: any) {
            console.error('WhatsApp Error:', err);
            setStatus({
                type: 'error',
                msg: err.message || 'Error al enviar',
                details: err.details
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTemplate = async () => {
        if (!newTemplateName || !newTemplateBody) {
            setStatus({ type: 'error', msg: 'Nombre y texto son requeridos' });
            return;
        }

        setCreatingTemplate(true);
        setStatus(null);

        try {
            const payload = {
                name: newTemplateName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
                language: newTemplateLang,
                category: newTemplateCategory,
                components: [
                    {
                        type: 'BODY',
                        text: newTemplateBody
                    }
                ]
            };

            const { data, error } = await supabase.functions.invoke('whatsapp-templates', {
                method: 'POST',
                body: payload
            });

            if (error) throw error;

            if (data?.success) {
                setStatus({ type: 'success', msg: 'Plantilla creada con éxito. Deberá ser aprobada por Meta.' });
                setShowCreateMode(false);
                setNewTemplateName('');
                setNewTemplateBody('');
                await fetchTemplates(); // Refresh templates
            } else {
                throw new Error(data?.error || 'Error creando plantilla');
            }

        } catch (err: any) {
            console.error('Create Template Error:', err);
            setStatus({
                type: 'error',
                msg: err.message || 'Error al crear',
                details: err.details
            });
        } finally {
            setCreatingTemplate(false);
        }
    };

    return (
        <div className="whatsapp-test-container">
            <div className="whatsapp-test-card" style={{ maxWidth: '600px', width: '100%' }}>
                <div className="whatsapp-test-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <MessageSquare className="header-icon" />
                        <h2 style={{ margin: 0 }}>
                            {showCreateMode ? 'Crear Plantilla WhatsApp' : 'Prueba de Plantillas Reales'}
                        </h2>
                    </div>
                    <button
                        onClick={() => {
                            setShowCreateMode(!showCreateMode);
                            setStatus(null);
                        }}
                        style={{ background: 'none', border: '1px solid #ddd', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        {showCreateMode ? "Volver a Enviar" : <><Plus size={14} /> Crear Nueva</>}
                    </button>
                </div>

                {showCreateMode ? (
                    <div className="whatsapp-form">
                        <div className="input-group">
                            <label>Nombre de la Plantilla (sin espacios, usa guiones bajos)</label>
                            <input
                                type="text"
                                placeholder="ej: oferta_verano_2026"
                                value={newTemplateName}
                                onChange={(e) => setNewTemplateName(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div className="input-row" style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                            <div style={{ flex: 1 }}>
                                <label>Categoría</label>
                                <select
                                    value={newTemplateCategory}
                                    onChange={(e) => setNewTemplateCategory(e.target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                >
                                    <option value="MARKETING">MARKETING (Promociones, Novedades)</option>
                                    <option value="UTILITY">UTILITIES (Confirmaciones, Avisos de Info)</option>
                                    <option value="AUTHENTICATION">AUTHENTICATION</option>
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label><Globe size={12} /> Idioma</label>
                                <input
                                    type="text"
                                    value={newTemplateLang}
                                    onChange={(e) => setNewTemplateLang(e.target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                />
                            </div>
                        </div>
                        <div className="input-group" style={{ marginTop: '15px' }}>
                            <label><FileText size={14} /> Cuerpo del mensaje (Usa {"{{1}}"}, {"{{2}}"} para variables)</label>
                            <textarea
                                rows={5}
                                placeholder="Hola {{1}}, gracias por tu compra. Tu pedido {{2}} está confirmado."
                                value={newTemplateBody}
                                onChange={(e) => setNewTemplateBody(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', resize: 'vertical' }}
                            />
                        </div>

                        {status && (
                            <div className={`status-alert ${status.type}`} style={{ padding: '12px', borderRadius: '4px', marginTop: '15px', background: status.type === 'success' ? '#e6f4ea' : '#fce8e6', color: status.type === 'success' ? '#1e8e3e' : '#d93025' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {status.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                                    <span style={{ fontSize: '14px', fontWeight: '500' }}>{status.msg}</span>
                                </div>
                                {status.details && <pre style={{ fontSize: '10px', overflowX: 'auto', background: 'rgba(0,0,0,0.05)', padding: '5px', borderRadius: '4px' }}>{JSON.stringify(status.details, null, 2)}</pre>}
                            </div>
                        )}

                        <button
                            className="send-btn"
                            onClick={handleCreateTemplate}
                            disabled={creatingTemplate || !newTemplateName || !newTemplateBody}
                            style={{ marginTop: '20px', width: '100%', padding: '12px', background: '#1a73e8', color: 'white', border: 'none', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                        >
                            {creatingTemplate ? 'Creando...' : <><Plus size={18} /> Crear Plantilla</>}
                        </button>
                    </div>
                ) : (
                    <div className="whatsapp-form">
                        {loadingTemplates ? (
                            <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>Cargando tus plantillas desde Meta...</p>
                        ) : templates.length === 0 ? (
                            <p style={{ textAlign: 'center', color: '#d93025', padding: '20px' }}>No se encontraron plantillas aprobadas.</p>
                        ) : (
                            <>
                                <div className="input-group" style={{ marginBottom: '15px' }}>
                                    <label style={{ fontSize: '13px', color: '#666' }}> <LayoutTemplate size={12} /> Plantilla (Traídas de Meta)</label>
                                    <select
                                        value={templateId}
                                        onChange={(e) => handleTemplateChange(e.target.value)}
                                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    >
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
                                        ))}
                                    </select>
                                    {selectedTemplate && (
                                        <div style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', marginTop: '10px', fontSize: '13px', color: '#444' }}>
                                            <strong>Vista Previa (BODY):</strong>
                                            <p style={{ margin: '5px 0 0 0', whiteSpace: 'pre-wrap' }}>
                                                {selectedTemplate.components.find(c => c.type === 'BODY')?.text || 'Sin texto'}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="input-group">
                                    <label><Smartphone size={14} /> Número de Teléfono Prueba</label>
                                    <input
                                        type="text"
                                        placeholder="5491122334455"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    />
                                </div>

                                {selectedTemplate && selectedTemplate.parsedVariables && selectedTemplate.parsedVariables.length > 0 && (
                                    <div className="variables-section" style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginTop: '15px' }}>
                                        <h4 style={{ margin: '0 0 15px', fontSize: '14px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Hash size={16} /> Parámetros detectados en esta plantilla ({selectedTemplate.parsedVariables.length})
                                        </h4>

                                        {selectedTemplate.parsedVariables.map((v, idx) => (
                                            <div className="input-group" key={v.key} style={{ marginBottom: '12px' }}>
                                                <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                                                    {v.label}
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder={v.placeholder}
                                                    value={vars[v.key] || ''}
                                                    onChange={(e) => setVars({ ...vars, [v.key]: e.target.value })}
                                                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {status && (
                                    <div className={`status-alert ${status.type}`} style={{ padding: '12px', borderRadius: '4px', marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '5px', background: status.type === 'success' ? '#e6f4ea' : '#fce8e6', color: status.type === 'success' ? '#1e8e3e' : '#d93025' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {status.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                                            <span style={{ fontSize: '14px', fontWeight: '500' }}>{status.msg}</span>
                                        </div>
                                        {status.details && <pre style={{ fontSize: '10px', overflowX: 'auto', background: 'rgba(0,0,0,0.05)', padding: '5px', borderRadius: '4px' }}>{JSON.stringify(status.details, null, 2)}</pre>}
                                    </div>
                                )}

                                <button
                                    className="send-btn"
                                    onClick={handleSendTest}
                                    disabled={loading}
                                    style={{ marginTop: '20px', width: '100%', padding: '12px', background: '#25D366', color: 'white', border: 'none', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                                >
                                    {loading ? 'Enviando...' : <><Send size={18} /> Enviar Prueba De Plantilla Directa</>}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
